/**
 * RELATÓRIO DE RECEITAS E DESPESAS POR PERÍODO (P3, fase 4) — para o contador
 * e o imposto de renda. Só contas e formatos, sem tela; testado em
 * testes/relatorio.mjs.
 *
 * NÃO é livro-caixa nem LCDPR (decisão do gestor). Se a fazenda for obrigada
 * ao LCDPR, esta tabela serve de base — a confirmar com o contador.
 *
 * Nada aqui é dado novo: tudo sai do que já está no aparelho do gestor (as
 * fichas, os pagamentos efetivados, as comissões, o extrato, a catira e as
 * notas). Sem migração, sem servidor.
 *
 * REGIME DE CAIXA sempre que o app tem a data em que o dinheiro mudou de mão
 * (é o que o IRPF da atividade rural usa):
 *   - a quitação da ficha (pagamento efetivado pelo gestor), na data do «pago em»;
 *   - a comissão a pagar ao comprador e a despesa de viagem por conta da
 *     empresa, na data do acerto em que foram pagas (2F).
 * Onde o app NÃO tem essa data, a linha usa a data do documento (a ficha, o
 * movimento, a nota) e sai marcada «previsto/sem data de pagamento» — o
 * contador decide o que fazer com ela, em vez de o app fingir que sabe.
 *
 * O QUE ENTRA
 *   Despesas: compra de gado (as quitações; o que falta pagar vai como «a
 *   pagar» na data do próximo vencimento) · frete e gastos da ficha · comissão
 *   a pagar ao comprador · despesa de viagem POR CONTA DA EMPRESA · recria da
 *   catira · nota de entrada sem outra ligação.
 *   Receitas: venda de gado (as quitações da ficha de Venda são o recebimento)
 *   · comissão a receber do cliente (3B) · revenda (3F: os recebimentos do
 *   comprador final; o que falta vai «a receber» na data da ficha) · nota de
 *   saída sem outra ligação.
 *
 * 3F · INTERMEDIAÇÃO × REVENDA (decisão do gestor, 06/10/2026):
 *   - porteira Cliente (intermediação): o cliente paga o vendedor direto. Para
 *     o gestor entra só a comissão a receber; a parte do vendedor daquela
 *     porteira fica FORA das despesas (o «a pagar» é só a parte do gestor);
 *   - porteira Revenda: a compra é despesa (pelas quitações, como sempre) e a
 *     venda é receita (pelos recebimentos).
 *   Fecha a pendência do P3 «quem paga o vendedor quando a compra é para cliente».
 *
 * O QUE NÃO ENTRA, DE PROPÓSITO
 *   - ajuste de contagem da catira (encontradas, sumidos, zeragem): é
 *     resultado, não dinheiro;
 *   - vale (adiantamento): é dinheiro emprestado ao comprador, que se acerta
 *     no extrato;
 *   - despesa de viagem por conta do comprador: não é dinheiro da empresa;
 *   - entrada e venda da catira: já estão nas fichas (contaria duas vezes).
 *
 * SEM DUPLICAR: nota ligada a uma ficha, despesa ou movimento NÃO vira linha;
 * a chave dela vai para a coluna Documento da linha do registro. Só a nota
 * solta (sem ligação) vira linha própria.
 *
 * Toda soma em centavos inteiros: os totais fecham ao centavo.
 */
import { calcular, centavos, gastosValendo, valorDaComissao } from './contas'
import { gastosDoGestor, porteirasDa } from './destinos'
import { contasDaFicha } from './financeiro'
import { estaAcertado, type Acerto, type Lancamento } from './extrato'
import type { Movimento } from './catira'
import { formatarChave, infoDaChave, nomeDoModelo, notaValendo, descricaoDaNota, documentoDaNota, type Nota } from './notas'
import { dataBR, diaDe, MESES, reais } from './numeros'
import type { Aba, Celula } from './planilha'
import {
  NOME_COMISSAO_CLIENTE, NOME_COMISSAO_COMPRADOR,
  type Comissao, type ComissaoCliente, type Ficha, type PagamentoDeComissao, type Quitacao, type Recebimento, type VendaDaPorteira,
} from './tipos'

/* ---------------------------------------------------------------------------
 * Tipos
 * ------------------------------------------------------------------------- */

/** Tudo o que o relatório lê — o que já mora no aparelho do gestor (ou da Consulta). */
export interface DadosDoRelatorio {
  fichas: Ficha[]
  quitacoes: Quitacao[]
  /** «comissão a pagar (comprador)», por id da ficha */
  comissoes: Record<string, Comissao>
  /** «comissão a receber (cliente)» — a lista ou o mapa do armazém de pagamentos */
  comissoesCliente: ComissaoCliente[] | Record<string, ComissaoCliente>
  lancamentos: Lancamento[]
  acertos: Acerto[]
  movimentos: Movimento[]
  notas: Nota[]
  /** 3F · tabela de venda das porteiras Revenda — a lista ou o mapa do armazém de pagamentos */
  vendas?: VendaDaPorteira[] | Record<string, VendaDaPorteira>
  /** 3F · o que o comprador final pagou ao gestor */
  recebimentos?: Recebimento[]
  /** R2B · o que o cliente pagou da comissão (migração 014) */
  recebimentosComissao?: Recebimento[]
  /** R2B · a comissão do comprador paga fora do acerto (migração 014) */
  pagamentosComissao?: PagamentoDeComissao[]
}

export type TipoDaLinha = 'receita' | 'despesa'

/**
 * - pago: o dinheiro mudou de mão nesta data (quitação, acerto);
 * - aberto: o que falta pagar (ou receber) de uma ficha, na data do próximo vencimento;
 * - previsto: o app não tem a data do pagamento; vale a data do documento.
 */
export type SituacaoDaLinha = 'pago' | 'aberto' | 'previsto'

/** R2B: as comissões com os dois nomes fixos de todas as telas */
export type Categoria =
  | 'Compra de gado' | 'Frete e gastos' | typeof NOME_COMISSAO_COMPRADOR | 'Despesa de viagem' | 'Recria' | 'Nota de entrada'
  | 'Venda de gado' | typeof NOME_COMISSAO_CLIENTE | 'Revenda' | 'Nota de saída'

export interface LinhaDoRelatorio {
  /** único e estável: a mesma linha tem o mesmo id em toda exportação */
  id: string
  data: string
  tipo: TipoDaLinha
  categoria: Categoria
  descricao: string
  /** sempre positivo; o tipo diz se soma ou tira */
  valor: number
  situacao: SituacaoDaLinha
  /** número da ficha, quando há */
  ficha: string
  /** id da ficha (a tela abre a ficha com um toque) */
  fichaId?: string
  /** chave(s) da(s) nota(s) ligada(s), formatada(s) em blocos de 4 (a coluna da planilha) */
  chave: string
  /** R2B · a(s) nota(s) em poucas letras para a tela: «NF-e 4567» (a chave inteira fica na planilha) */
  notaCurta?: string
  /** o produtor (vendedor), o cliente ou o comprador da despesa */
  pessoa: string
  /** ficha + nota + pessoa numa frase só (a coluna «Documento» da tela) */
  documento: string
}

/* ---------------------------------------------------------------------------
 * Textos
 * ------------------------------------------------------------------------- */

export function textoDaSituacao(l: Pick<LinhaDoRelatorio, 'situacao' | 'tipo'>): string {
  if (l.situacao === 'pago') return l.tipo === 'receita' ? 'recebido' : 'pago'
  if (l.situacao === 'aberto') return l.tipo === 'receita' ? 'a receber' : 'a pagar'
  return 'previsto/sem data de pagamento'
}

/** A nota do rodapé da tela e da planilha: o que entra, o que não entra e o porquê. */
export const NOTA_DO_RODAPE = [
  'Dois totais, que não se somam: «Realizado (caixa)» é o dinheiro que mudou de mão no período; «Previsto» é o que ainda falta pagar ou receber, ou não tem data de pagamento no app.',
  'Regime de caixa: pagamentos e recebimentos efetivados entram na data em que foram pagos; fretes e gastos, na data do «pago em»; a comissão do comprador, na data do acerto (ou do pagamento fora do acerto); as despesas de viagem por conta da empresa, na data do acerto. Comissão já paga entra mesmo que a ficha seja reaberta ou cancelada depois.',
  'Onde o app não tem a data do pagamento, a linha usa a data do documento e sai como «previsto/sem data de pagamento». O que falta pagar ou receber de uma ficha encerrada sai como «a pagar» ou «a receber», na data do próximo vencimento (ou da ficha).',
  'Compra para cliente (intermediação): o cliente paga o vendedor direto, então só a Comissão do cliente (você recebe) entra, como receita; a parte do vendedor daquela porteira não é despesa. Revenda: a compra entra como despesa (pelos pagamentos ao vendedor) e a venda como receita (pelos recebimentos do comprador final); o que falta receber sai «a receber», na data da ficha.',
  'Não entram: ajustes de contagem do Meu estoque (encontradas, sumidos, zeragem), porque são resultado e não dinheiro; vales (adiantamentos), porque são dinheiro emprestado ao comprador e se acertam no extrato; despesas de viagem por conta do comprador, porque não são dinheiro da empresa.',
  'Nota fiscal ligada a uma ficha, despesa ou movimento não vira linha a mais: a chave dela aparece na coluna Documento. Só as notas sem ligação viram linha própria.',
  'Este relatório não é livro-caixa nem LCDPR. Se a fazenda for obrigada ao LCDPR, ele serve de base — a confirmar com o contador.',
]

/* ---------------------------------------------------------------------------
 * As linhas
 * ------------------------------------------------------------------------- */

const emC = (v: number) => Math.round(v * 100)

function documento(ficha: string, nota: string, pessoa: string): string {
  return [ficha ? `Ficha ${ficha}` : '', nota, pessoa].filter(Boolean).join(' · ')
}

/** R2B · «NF-e 4567» em vez da chave de 44 números (a chave inteira fica na planilha). */
export function notaCurta(n: Pick<Nota, 'chave' | 'numero' | 'serie' | 'origem'>): string {
  if (!n.chave) return documentoDaNota(n)
  const numero = n.numero || infoDaChave(n.chave)?.numero || ''
  return `${nomeDoModelo(n.chave).replace(' (cupom)', '')}${numero ? ` ${numero}` : ''}`
}

function listaDeComissoesCliente(c: DadosDoRelatorio['comissoesCliente']): ComissaoCliente[] {
  return Array.isArray(c) ? c : Object.values(c ?? {})
}

/**
 * Todas as linhas com data entre `de` e `ate` (inclusive), em ordem de data.
 * Ficha cancelada ou excluída não gera conta nenhuma — só o pagamento que de
 * fato saiu (quitação valendo) continua aparecendo: dinheiro pago é pago.
 */
export function linhasDoRelatorio(dados: DadosDoRelatorio, de: string, ate: string): LinhaDoRelatorio[] {
  const linhas: LinhaDoRelatorio[] = []
  const acertoPorId = new Map(dados.acertos.map((a) => [a.id, a]))
  const movPorId = new Map(dados.movimentos.map((m) => [m.id, m]))

  // as notas ligadas, por registro: a chave vai para o Documento da linha dele
  const chavesDe = new Map<string, string[]>()
  const curtasDe = new Map<string, string[]>()
  const pendurar = (k: string, n: Nota) => {
    const texto = n.chave ? formatarChave(n.chave) : documentoDaNota(n)
    chavesDe.set(k, [...(chavesDe.get(k) ?? []), texto])
    curtasDe.set(k, [...(curtasDe.get(k) ?? []), notaCurta(n)])
  }
  for (const n of dados.notas) {
    if (!notaValendo(n) || !n.ligacao) continue
    if (n.ligacao.tipo === 'catira') {
      // nota da venda da catira: a venda é a da ficha (a catira não vira linha)
      const m = movPorId.get(n.ligacao.id)
      pendurar(m && m.tipo !== 'recria' && m.fichaId ? `ficha:${m.fichaId}` : `catira:${n.ligacao.id}`, n)
    } else pendurar(`${n.ligacao.tipo}:${n.ligacao.id}`, n)
  }
  const chave = (k: string) => (chavesDe.get(k) ?? []).join('; ')
  const curta = (k: string) => (curtasDe.get(k) ?? []).join('; ')

  /** `notaDe`: o registro cujas notas vão para o Documento (curtas) — a chave inteira já vem em `chave` */
  const por = (l: Omit<LinhaDoRelatorio, 'documento'> & { notaDe?: string }) => {
    const { notaDe, ...resto } = l
    const nc = notaDe && l.chave ? curta(notaDe) : ''
    linhas.push({ ...resto, valor: centavos(l.valor), notaCurta: nc, documento: documento(l.ficha, nc, l.pessoa) })
  }

  /** dia local do acerto (o «feito em» é um instante) */
  const diaDoAcerto = (a: Acerto) => diaDe(a.feitoEm)

  /* ---------- fichas: compra, venda, gastos, comissões, revenda ---------- */
  // R2B: o que FALTA pagar ou receber sai de `contasDaFicha` (src/dominio/financeiro.ts),
  // a mesma conta das Fichas, do Pagamento, do Custo, da tela Contas e do Excel.
  // Aqui só se acrescenta o que já foi pago, cada pagamento na sua data.
  const quitacoesPorFicha = new Map<string, Quitacao[]>()
  for (const q of dados.quitacoes) quitacoesPorFicha.set(q.fichaId, [...(quitacoesPorFicha.get(q.fichaId) ?? []), q])
  const ccs = listaDeComissoesCliente(dados.comissoesCliente)
  const vendas = Array.isArray(dados.vendas) ? dados.vendas : Object.values(dados.vendas ?? {})
  // 3F: recebido é o que vale (anulado ou recusado pelo servidor não entrou)
  const recebimentos = (dados.recebimentos ?? []).filter((x) => !x.anuladoEm && !x.recusado)
  const recebimentosComissao = (dados.recebimentosComissao ?? []).filter((x) => !x.anuladoEm && !x.recusado)
  const pagamentosComissao = (dados.pagamentosComissao ?? []).filter((x) => !x.anuladoEm && !x.recusado)
  // acerto ANULADO solta a comissão (o banco zera o acerto_id; aqui, se o aparelho
  // ainda tem o id velho, a conta vê a comissão em aberto do mesmo jeito)
  const comissoesValendo = Object.fromEntries(Object.entries(dados.comissoes ?? {}).map(([k, c]) => {
    const ac = c.acertoId ? acertoPorId.get(c.acertoId) : undefined
    return [k, ac?.anulacao ? { ...c, acertoId: null } : c]
  }))
  const financeiro = {
    quitacoes: dados.quitacoes, comissoes: comissoesValendo, comissoesCliente: ccs, vendas,
    recebimentos: dados.recebimentos ?? [], recebimentosComissao: dados.recebimentosComissao ?? [], pagamentosComissao: dados.pagamentosComissao ?? [],
  }

  for (const f of dados.fichas) {
    if (f.finalidade !== 'Compra' && f.finalidade !== 'Venda') continue
    const compra = f.finalidade === 'Compra'
    const tipo: TipoDaLinha = compra ? 'despesa' : 'receita'
    const valendo = f.situacao === 'aberta' || f.situacao === 'encerrada'
    const encerrada = f.situacao === 'encerrada'
    const r = calcular(f)
    const kFicha = `ficha:${f.id}`
    const k = chave(kFicha)
    const base = { ficha: f.numero, fichaId: f.id, pessoa: f.produtor.nome, notaDe: kFicha }
    const quem = f.produtor.nome || 'sem produtor'
    const nomeDaPorteira = (id?: string) => porteirasDa(f).find((p) => p.id === id)?.nome?.trim()

    // 1. o dinheiro que mudou de mão — DINHEIRO PAGO É PAGO: entra mesmo de ficha
    //    reaberta ou cancelada depois. As quitações valendo, cada uma na sua data
    //    (na ficha de Venda, a quitação é o recebimento do comprador)
    const qs = (quitacoesPorFicha.get(f.id) ?? []).filter((q) => !q.anuladaEm && !q.recusada)
    qs.forEach((q) => {
      por({
        id: `quitacao:${q.id}`, data: q.pagoEm, tipo, categoria: compra ? 'Compra de gado' : 'Venda de gado',
        descricao: `${compra ? 'Pagamento ao vendedor' : 'Recebimento da venda'} — ${quem}${q.obs ? ` (${q.obs})` : ''}`,
        valor: q.valor, situacao: 'pago', chave: k, ...base,
      })
    })
    // 3F · o que o comprador final já pagou ao gestor (revenda)
    recebimentos.filter((x) => x.fichaId === f.id).forEach((x) => {
      const nome = nomeDaPorteira(x.destinoId) || 'comprador final'
      por({
        id: `recebimento:${x.id}`, data: x.recebidoEm, tipo: 'receita', categoria: 'Revenda',
        descricao: `Recebimento da revenda — ${nome}${x.obs ? ` (${x.obs})` : ''}`,
        valor: x.valor, situacao: 'pago', chave: '', ficha: f.numero, fichaId: f.id, pessoa: nome,
      })
    })
    // R2B · o que o cliente já pagou da comissão (014)
    recebimentosComissao.filter((x) => x.fichaId === f.id).forEach((x) => {
      const nome = nomeDaPorteira(x.destinoId) || 'cliente'
      por({
        id: `recebimento-comissao:${x.id}`, data: x.recebidoEm, tipo: 'receita', categoria: NOME_COMISSAO_CLIENTE,
        descricao: `Recebido do cliente ${nome}${x.obs ? ` (${x.obs})` : ''}`,
        valor: x.valor, situacao: 'pago', chave: '', ficha: f.numero, fichaId: f.id, pessoa: nome,
      })
    })
    // 2. Comissão do comprador (você paga): paga no acerto (2F) ou fora dele (R2B, 014).
    //    Já paga entra SEMPRE, mesmo se a ficha foi reaberta ou cancelada depois
    const com = dados.comissoes[f.id]
    const ac = com?.acertoId ? acertoPorId.get(com.acertoId) : undefined
    const acertada = !!ac && !ac.anulacao
    if (com && com.porCabeca > 0 && acertada) {
      const vc = valorDaComissao(com, r.cab)
      por({
        id: `comissao:${f.id}`, data: diaDoAcerto(ac!), tipo: 'despesa', categoria: NOME_COMISSAO_COMPRADOR,
        descricao: `Comissão do comprador${f.pesadoPor ? ` ${f.pesadoPor}` : ''} — ${vc.cab} cab × ${reais(com.porCabeca)}, paga no acerto`,
        valor: vc.valor, situacao: 'pago', chave: '', ...base,
      })
    }
    pagamentosComissao.filter((x) => x.fichaId === f.id).forEach((x) => {
      const para = com?.beneficiario?.trim() || f.pesadoPor || 'comprador'
      por({
        id: `pagamento-comissao:${x.id}`, data: x.pagoEm, tipo: 'despesa', categoria: NOME_COMISSAO_COMPRADOR,
        descricao: `Comissão paga a ${para}${x.obs ? ` (${x.obs})` : ''}`,
        valor: x.valor, situacao: 'pago', chave: '', ...base,
      })
    })
    // R2B · frete e gastos PAGOS: na data do «pago em» (a parte do gestor)
    const doGestor = gastosDoGestor(f)
    gastosValendo(f).forEach((c) => {
      const valor = doGestor.get(c.id) ?? c.valor
      if (!(valor > 0) || !c.pagoEm) return
      const onde = nomeDaPorteira(c.destinoId)
      por({
        id: `gasto:${f.id}:${c.id}`, data: c.pagoEm, tipo: 'despesa', categoria: 'Frete e gastos',
        descricao: `${c.descricao || 'Gasto'}${c.favorecido ? ` — ${c.favorecido}` : ''} — ficha de ${quem}${onde ? ` (${onde})` : ''}${valor !== c.valor ? ` (sua parte de ${reais(c.valor)})` : ''}`,
        valor, situacao: 'pago', chave: '', ...base,
      })
    })
    if (!valendo) continue

    // 3. ficha ABERTA: os gastos ainda sem pagamento, previstos na data da ficha
    if (!encerrada) {
      gastosValendo(f).forEach((c) => {
        const valor = doGestor.get(c.id) ?? c.valor
        if (!(valor > 0) || c.pagoEm) return
        const onde = nomeDaPorteira(c.destinoId)
        por({
          id: `gasto:${f.id}:${c.id}`, data: c.vencimento || f.data, tipo: 'despesa', categoria: 'Frete e gastos',
          descricao: `${c.descricao || 'Gasto'} — ficha de ${quem}${onde ? ` (${onde})` : ''}${valor !== c.valor ? ` (sua parte de ${reais(c.valor)})` : ''}`,
          valor, situacao: 'previsto', chave: '', ...base,
        })
      })
      continue
    }

    // 4. ficha ENCERRADA: o que falta, conta a conta — o mesmo número das outras telas
    for (const ct of contasDaFicha(f, financeiro, { verComissao: true })) {
      if (!(ct.falta > 0)) continue
      const data = ct.vencimento || f.data
      if (ct.tipo === 'vendedor' || ct.tipo === 'comprador') {
        const parte = ct.descricao.includes('a cargo do cliente') ? ct.descricao.slice(ct.descricao.indexOf('(')) : ''
        por({
          id: `aberto:${f.id}`, data, tipo, categoria: compra ? 'Compra de gado' : 'Venda de gado',
          descricao: `${compra ? 'A pagar ao vendedor' : 'A receber da venda'} — ${quem}${parte ? ` ${parte}` : ct.pago ? ` (líquido ${reais(r.liquido)})` : ''}`,
          valor: ct.falta, situacao: 'aberto', chave: k, ...base,
        })
      } else if (ct.tipo === 'gasto') {
        const c = f.custos.find((x) => x.id === ct.gastoId)
        const onde = nomeDaPorteira(c?.destinoId)
        por({
          id: `gasto:${f.id}:${ct.gastoId}`, data, tipo: 'despesa', categoria: 'Frete e gastos',
          descricao: `${ct.descricao}${c?.favorecido ? ` — ${c.favorecido}` : ''} — ficha de ${quem}${onde ? ` (${onde})` : ''}`,
          valor: ct.falta, situacao: 'aberto', chave: '', ...base,
        })
      } else if (ct.tipo === 'comissaoComprador') {
        const vc = com ? valorDaComissao(com, r.cab) : null
        por({
          id: `comissao:${f.id}`, data, tipo: 'despesa', categoria: NOME_COMISSAO_COMPRADOR,
          descricao: `Comissão do comprador ${ct.quem}${vc && com ? ` — ${vc.cab} cab × ${reais(com.porCabeca)}` : ''}${ct.pago ? ` (falta de ${reais(ct.valor)})` : ''}`,
          valor: ct.falta, situacao: 'aberto', chave: '', ...base,
        })
      } else if (ct.tipo === 'comissaoCliente') {
        const cc = ccs.find((x) => x.fichaId === f.id && x.destinoId === ct.destinoId)
        const cab = cc && cc.porCabeca ? Math.round(ct.valor / cc.porCabeca) : 0
        por({
          id: `comissao-cliente:${f.id}:${ct.destinoId}`, data, tipo: 'receita', categoria: NOME_COMISSAO_CLIENTE,
          descricao: `Comissão do cliente ${ct.quem}${cc ? ` — ${cab} cab × ${reais(cc.porCabeca)}` : ''}${ct.pago ? ` (falta de ${reais(ct.valor)})` : ''}`,
          valor: ct.falta, situacao: 'aberto', chave: '', ficha: f.numero, fichaId: f.id, pessoa: ct.quem,
        })
      } else if (ct.tipo === 'revenda') {
        const cab = ct.descricao.replace(/^Revenda — /, '')
        por({
          id: `revenda:${f.id}:${ct.destinoId}`, data, tipo: 'receita', categoria: 'Revenda',
          descricao: `A receber da revenda — ${ct.quem} (${cab}${ct.pago ? `; venda ${reais(ct.valor)}` : ''})`,
          valor: ct.falta, situacao: 'aberto', chave: '', ficha: f.numero, fichaId: f.id, pessoa: ct.quem,
        })
      }
    }
  }

  /* ---------- despesas de viagem por conta da empresa ---------- */
  for (const l of dados.lancamentos) {
    // vale é empréstimo ao comprador; por conta dele não é dinheiro da empresa
    if (l.tipo !== 'despesa' || l.porConta !== 'gestor' || l.anulacao || l.recusa) continue
    const ac = l.acertoId ? acertoPorId.get(l.acertoId) : undefined
    const pago = !!ac && estaAcertado(l, dados.acertos)
    por({
      id: `despesa:${l.id}`, data: pago ? diaDoAcerto(ac!) : l.data, tipo: 'despesa', categoria: 'Despesa de viagem',
      descricao: `${l.categoria || 'Despesa'}${l.descricao ? ` — ${l.descricao}` : ''} (de ${dataBR(l.data)})`,
      valor: l.valor, situacao: pago ? 'pago' : 'previsto', chave: chave(`despesa:${l.id}`), notaDe: `despesa:${l.id}`, ficha: '', pessoa: l.compradorNome,
    })
  }

  /* ---------- recria da catira (o resto da catira já está nas fichas ou é resultado) ---------- */
  for (const m of dados.movimentos) {
    if (m.tipo !== 'recria' || m.anulacao || m.recusa || !(m.valor > 0)) continue
    por({
      id: `recria:${m.id}`, data: m.data, tipo: 'despesa', categoria: 'Recria',
      descricao: `Recria${m.categoria ? ` — ${m.categoria}` : ''}${m.descricao ? `: ${m.descricao}` : ''}`,
      valor: m.valor, situacao: 'previsto', chave: chave(`catira:${m.id}`), notaDe: `catira:${m.id}`, ficha: '', pessoa: '',
    })
  }

  /* ---------- notas soltas (sem ligação) ---------- */
  for (const n of dados.notas) {
    if (!notaValendo(n) || n.ligacao || !(n.valor > 0)) continue
    const entrada = n.sentido === 'entrada'
    por({
      id: `nota:${n.id}`, data: n.data, tipo: entrada ? 'despesa' : 'receita', categoria: entrada ? 'Nota de entrada' : 'Nota de saída',
      descricao: `${descricaoDaNota(n) || 'Nota fiscal'} (${documentoDaNota(n)})`,
      valor: n.valor, situacao: 'previsto', chave: n.chave ? formatarChave(n.chave) : '', ficha: '',
      pessoa: entrada ? n.emitenteNome : n.destinatarioNome || n.emitenteNome,
    })
  }

  return linhas
    .filter((l) => l.data >= de && l.data <= ate)
    .sort((a, b) => a.data.localeCompare(b.data) || (a.tipo === b.tipo ? 0 : a.tipo === 'receita' ? -1 : 1)
      || a.categoria.localeCompare(b.categoria) || a.id.localeCompare(b.id))
}
/* ---------------------------------------------------------------------------
 * Totais — R2B: DOIS totais que não se somam (revisão de 06/10/2026: o
 * relatório somava o dinheiro que entrou com o que ainda vai entrar)
 * ------------------------------------------------------------------------- */

export interface Lado {
  receitas: number
  despesas: number
  /** receitas − despesas */
  saldo: number
}

export interface Totais {
  /** «Realizado (caixa)»: o dinheiro que mudou de mão (linhas «pago»/«recebido») */
  realizado: Lado
  /** «Previsto»: o que falta pagar ou receber e o que não tem data de pagamento */
  previsto: Lado
  linhas: number
}

export interface TotaisDoMes extends Totais {
  /** «2026-03» */
  mes: string
}

function somar(ls: LinhaDoRelatorio[]): Totais {
  let rec = 0, des = 0, recP = 0, desP = 0
  for (const l of ls) {
    const v = emC(l.valor)
    const previsto = l.situacao !== 'pago'
    if (l.tipo === 'receita') { if (previsto) recP += v; else rec += v } else if (previsto) desP += v; else des += v
  }
  return {
    realizado: { receitas: rec / 100, despesas: des / 100, saldo: (rec - des) / 100 },
    previsto: { receitas: recP / 100, despesas: desP / 100, saldo: (recP - desP) / 100 },
    linhas: ls.length,
  }
}

/** Os totais por mês (só os meses que têm linha) e os do período inteiro. */
export function totaisDoRelatorio(linhas: LinhaDoRelatorio[]): { meses: TotaisDoMes[]; periodo: Totais } {
  const porMes = new Map<string, LinhaDoRelatorio[]>()
  for (const l of linhas) {
    const m = l.data.slice(0, 7)
    porMes.set(m, [...(porMes.get(m) ?? []), l])
  }
  const meses = [...porMes.keys()].sort().map((m) => ({ mes: m, ...somar(porMes.get(m)!) }))
  return { meses, periodo: somar(linhas) }
}

/** «março de 2026» */
export function nomeDoMes(m: string): string {
  return `${MESES[Number(m.slice(5, 7)) - 1] ?? ''} de ${m.slice(0, 4)}`
}

/* ---------------------------------------------------------------------------
 * Planilha
 * ------------------------------------------------------------------------- */

/** O relatório em Excel: a aba das linhas e a aba dos totais por mês, com a nota do rodapé nas duas. */
export function abasDoRelatorio(linhas: LinhaDoRelatorio[], de: string, ate: string, empresa = ''): Aba[] {
  const t = totaisDoRelatorio(linhas)
  const titulo = `Receitas e despesas — ${dataBR(de)} a ${dataBR(ate)}${empresa ? ` — ${empresa}` : ''}`
  const L: Celula[][] = [
    [titulo],
    [],
    ['Data', 'Tipo', 'Categoria', 'Descrição', 'Receita (R$)', 'Despesa (R$)', 'Situação', 'Ficha', 'Chave da nota', 'Produtor / cliente'],
  ]
  const cab = L.length - 1
  for (const l of linhas) {
    L.push([dataBR(l.data), l.tipo === 'receita' ? 'Receita' : 'Despesa', l.categoria, l.descricao,
      l.tipo === 'receita' ? l.valor : null, l.tipo === 'despesa' ? l.valor : null, textoDaSituacao(l), l.ficha, l.chave, l.pessoa])
  }
  // R2B: dois totais, nunca somados
  const r = t.periodo.realizado, p = t.periodo.previsto
  L.push(['Realizado (caixa)', '', '', `pago e recebido no período — saldo ${reais(r.saldo)}`, r.receitas, r.despesas])
  const totR = L.length - 1
  L.push(['Previsto', '', '', `a pagar, a receber e sem data de pagamento — saldo ${reais(p.saldo)}`, p.receitas, p.despesas])
  const totP = L.length - 1
  L.push([], ...NOTA_DO_RODAPE.map((s) => [s] as Celula[]))

  const M: Celula[][] = [
    [titulo],
    [],
    ['Mês', 'Realizado: receitas (R$)', 'Realizado: despesas (R$)', 'Realizado: saldo (R$)',
      'Previsto: receitas (R$)', 'Previsto: despesas (R$)', 'Previsto: saldo (R$)', 'Linhas'],
  ]
  const cabM = M.length - 1
  const linhaDoMes = (nome: string, x: Totais): Celula[] =>
    [nome, x.realizado.receitas, x.realizado.despesas, x.realizado.saldo, x.previsto.receitas, x.previsto.despesas, x.previsto.saldo, x.linhas]
  for (const m of t.meses) M.push(linhaDoMes(nomeDoMes(m.mes), m))
  M.push(linhaDoMes('Período', t.periodo))
  const totM = M.length - 1
  M.push([], ...NOTA_DO_RODAPE.map((s) => [s] as Celula[]))

  return [
    { nome: 'Linhas', linhas: L, larguras: [18, 9, 30, 60, 14, 14, 28, 15, 58, 30], negrito: [0, cab, totR, totP] },
    { nome: 'Totais por mês', linhas: M, larguras: [22, 18, 18, 18, 18, 18, 18, 8], negrito: [0, cabM, totM] },
  ]
}
